import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { CreateObjectStepId } from 'entities/kyrgyzstan/model/create-object'
import {
  CreateObjectFieldCard,
  SelectableTile,
  SelectableTileGrid,
} from 'entities/kyrgyzstan/ui/create-object'

import type { CreateObjectDynamicFieldGroup } from 'shared/api/kyrgyzstan/create-object'
import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import { Button } from 'shared/ui/button'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'

import {
  areCreateObjectDynamicFieldValuesEqual,
  getCreateObjectAdditionalAmenitiesSelectedCount,
  getCreateObjectMainAmenityFields,
  hydrateCreateObjectDynamicFieldValues,
  serializeCreateObjectDynamicFieldValues,
} from '../../lib/dynamic-fields'
import type { CreateObjectDynamicFieldValues } from '../../lib/dynamic-fields'
import type { CreateObjectDraftStepScreenProps } from '../../types'
import { CreateObjectStepLayout } from '../create-object-step-layout'

import { AmenitiesStepSkeleton } from './ui/amenities-step-skeleton'

import s from './styles.module.scss'

type AmenitiesStepOperation =
  'idle' | 'loading' | 'submitting' | 'saving-additional'

export const AmenitiesStep = ({
  draftId,
  layoutProps,
  mode = 'create',
  onNext,
  onOpenStep,
}: CreateObjectDraftStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [fieldGroups, setFieldGroups] = useState<
    CreateObjectDynamicFieldGroup[]
  >([])
  const [fieldValues, setFieldValues] =
    useState<CreateObjectDynamicFieldValues>({})
  const [initialFieldValues, setInitialFieldValues] =
    useState<CreateObjectDynamicFieldValues>({})
  const [hasLoadedDynamicFields, setHasLoadedDynamicFields] = useState(false)
  const [operation, setOperation] = useState<AmenitiesStepOperation>('loading')

  useEffect(() => {
    const abortController = new AbortController()

    const fetchDynamicFields = async () => {
      setHasLoadedDynamicFields(false)
      setOperation('loading')

      try {
        const { data } = await KyrgyzstanCreateObjectApi.getDynamicFieldsStep(
          draftId,
          abortController.signal,
        )

        setFieldGroups(data.data.available_fields)
        const nextFieldValues = hydrateCreateObjectDynamicFieldValues(
          data.data.selected_fields,
        )

        setFieldValues(nextFieldValues)
        setInitialFieldValues(nextFieldValues)
        setHasLoadedDynamicFields(true)
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setOperation('idle')
        }
      }
    }

    fetchDynamicFields()

    return () => {
      abortController.abort()
    }
  }, [draftId, showApiErrorDialog])

  const saveDynamicFields = () =>
    KyrgyzstanCreateObjectApi.saveDynamicFieldsStep(
      draftId,
      serializeCreateObjectDynamicFieldValues(fieldValues, fieldGroups),
    )

  const handleOpenAdditionalAmenities = async () => {
    if (operation !== 'idle' || !hasLoadedDynamicFields) {
      return
    }

    setOperation('saving-additional')
    let shouldOpenAdditionalAmenities = false

    try {
      await saveDynamicFields()
      shouldOpenAdditionalAmenities = true
    } catch (error) {
      showApiErrorDialog(error)
    } finally {
      setOperation('idle')
    }

    if (shouldOpenAdditionalAmenities) {
      onOpenStep(CreateObjectStepId.AdditionalAmenities)
    }
  }

  const handleToggleAmenity = (fieldId: number) => {
    if (operation !== 'idle' || !hasLoadedDynamicFields) {
      return
    }

    setFieldValues((currentValues) => {
      const nextValues = { ...currentValues }

      if (nextValues[fieldId] === 'true') {
        delete nextValues[fieldId]
      } else {
        nextValues[fieldId] = 'true'
      }

      return nextValues
    })
  }

  const handleContinue = async () => {
    if (operation !== 'idle' || !hasLoadedDynamicFields) {
      return
    }

    setOperation('submitting')
    let shouldGoNext = false

    try {
      await saveDynamicFields()
      shouldGoNext = true
    } catch (error) {
      showApiErrorDialog(error)
    } finally {
      setOperation('idle')
    }

    if (shouldGoNext) {
      onNext()
    }
  }

  const mainAmenityFields = getCreateObjectMainAmenityFields(fieldGroups)
  const additionalAmenitiesSelectedCount =
    getCreateObjectAdditionalAmenitiesSelectedCount(fieldGroups, fieldValues)
  const hasLocalChanges = !areCreateObjectDynamicFieldValuesEqual(
    fieldValues,
    initialFieldValues,
    fieldGroups,
  )
  const isEditModePristine = mode === 'edit' && !hasLocalChanges
  const isSubmitDisabled =
    !hasLoadedDynamicFields || operation !== 'idle' || isEditModePristine
  const isAddMoreDisabled = operation !== 'idle' || !hasLoadedDynamicFields

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      continueDisabled={isSubmitDisabled}
      continueLoading={operation === 'submitting'}
      onContinue={handleContinue}
      title={t('kyrgyzstan_block.create_object.steps.amenities.title')}
    >
      <CreateObjectFieldCard
        title={t('kyrgyzstan_block.create_object.steps.amenities.helper')}
        description={t(
          'kyrgyzstan_block.create_object.steps.amenities.description',
        )}
      >
        {operation === 'loading' ? (
          <AmenitiesStepSkeleton />
        ) : (
          <SelectableTileGrid>
            {mainAmenityFields.map((field) => (
              <SelectableTile
                key={field.id}
                title={field.name}
                icon={field.icon ?? undefined}
                disabled={operation !== 'idle' || !hasLoadedDynamicFields}
                onClick={() => handleToggleAmenity(field.id)}
                isSelected={fieldValues[field.id] === 'true'}
              />
            ))}
          </SelectableTileGrid>
        )}

        {operation !== 'loading' && (
          <div className={s.addMore}>
            <Button
              type="button"
              variant="transparentBlue"
              className={clsx(s.addButton, 'semi-bold-17')}
              borderRadius="md"
              disabled={isAddMoreDisabled}
              loading={operation === 'saving-additional'}
              onClick={handleOpenAdditionalAmenities}
            >
              {t('kyrgyzstan_block.create_object.steps.amenities.add_more')}
            </Button>

            {additionalAmenitiesSelectedCount > 0 && (
              <p className={clsx(s.additionalCount, 'medium-12')}>
                {t(
                  'kyrgyzstan_block.create_object.steps.amenities.added_additional',
                  {
                    count: additionalAmenitiesSelectedCount,
                  },
                )}
              </p>
            )}
          </div>
        )}
      </CreateObjectFieldCard>
    </CreateObjectStepLayout>
  )
}
